import { useEffect, useState } from 'react';
import { Table, Group, TextInput, Select, Button, Pagination, Stack, Text, LoadingOverlay, Box, Spoiler } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useDispatch, useSelector } from 'react-redux';
import { getLogs, clearLogs } from '../../../redux/reducers/logSlice';
import { selectLogsSelector, selectLogLoading } from '../../../redux/selectors/logSelector';
import { LogLevel, Log } from '../../../models/log.model';
import { getAthensDate } from '../../../utils/helper.utils';
import colors from '../../../utils/styles';
import ConfirmationModal from '../admin-common/confirmation/confirmation.modal';

function LogViewer() {
    const dispatch = useDispatch();
    const { data: logs, total, page, per_page } = useSelector(selectLogsSelector);
    const loading = useSelector(selectLogLoading);

    const initialParams = {
        page: 1,
        per_page: 25,
        search: '',
        level: '',
        startDate: '',
        endDate: ''
    };

    const [params, setParams] = useState(initialParams);
    const [clearConfirmationOpened, setClearConfirmationOpened] = useState(false);

    const handlePageChange = (newPage: number) => {
        const newParams = { ...params, page: newPage };
        setParams(newParams);
        dispatch(getLogs(newParams as any));
    };

    const handleSearch = (search: string) => {
        const newParams = { ...params, search, page: 1 };
        setParams(newParams);
        dispatch(getLogs(newParams as any));
    };

    const handleLevelChange = (level: string) => {
        const newParams = { ...params, level, page: 1 };
        setParams(newParams);
        dispatch(getLogs(newParams as any));
    };

    const handleDateChange = (dates: [Date | null, Date | null]) => {
        const [startDate, endDate] = dates;
        const newParams = {
            ...params,
            startDate: startDate ? getAthensDate(startDate) : '',
            endDate: endDate ? getAthensDate(endDate) : '',
            page: 1
        };
        setParams(newParams as any);
        dispatch(getLogs(newParams as any));
    };

    const handleClearLogs = () => {
        dispatch(clearLogs());
        setClearConfirmationOpened(false);
        dispatch(getLogs(initialParams as any));
    };

    const getLevelColor = (level: LogLevel) => {
        const colors = {
            [LogLevel.INFO]: 'blue',
            [LogLevel.WARNING]: 'yellow',
            [LogLevel.ERROR]: 'red',
            [LogLevel.DEBUG]: 'gray'
        };
        return colors[level];
    };

    useEffect(() => {
        dispatch(getLogs(initialParams as any));
    }, []);

    return (
        <>
            <ConfirmationModal
                opened={clearConfirmationOpened}
                onClose={() => setClearConfirmationOpened(false)}
                onConfirm={handleClearLogs}
                text="Are you sure you want to clear all logs? This action cannot be undone."
            />

            <Stack>
                <Group justify="space-between">
                    <Group>
                        <TextInput
                            placeholder="Search logs..."
                            value={params.search}
                            onChange={(e) => handleSearch(e.currentTarget.value)}
                        />
                        <Select
                            placeholder="Filter by level"
                            value={params.level}
                            onChange={(value) => handleLevelChange(value || '')}
                            data={[
                                { value: '', label: 'All' },
                                ...Object.values(LogLevel).map(level => ({
                                    value: level,
                                    label: level.charAt(0).toUpperCase() + level.slice(1)
                                }))
                            ]}
                        />
                        <DatePickerInput
                            type="range"
                            placeholder="Pick date range"
                            value={[
                                params.startDate ? new Date(params.startDate) : null,
                                params.endDate ? new Date(params.endDate) : null
                            ]}
                            onChange={handleDateChange}
                        />
                    </Group>
                    <Button
                        color="red"
                        variant="outline"
                        onClick={() => setClearConfirmationOpened(true)}
                    >
                        Clear All Logs
                    </Button>
                </Group>

                <Box style={{ position: 'relative' }}>
                    <LoadingOverlay
                        visible={loading}
                        overlayProps={{ radius: "md", blur: 0.5 }}
                        loaderProps={{ color: colors.appDarkBlue, type: 'bars' }}
                    />
                    <Table>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Timestamp</Table.Th>
                                <Table.Th>Level</Table.Th>
                                <Table.Th>Message</Table.Th>
                                <Table.Th>Source</Table.Th>
                                <Table.Th>User</Table.Th>
                                <Table.Th>Metadata</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {logs?.map((log: Log) => (
                                <Table.Tr key={log._id}>
                                    <Table.Td>{new Date(log.timestamp).toLocaleString()}</Table.Td>
                                    <Table.Td>
                                        <Text color={getLevelColor(log.level)}>
                                            {log.level.toUpperCase()}
                                        </Text>
                                    </Table.Td>
                                    <Table.Td>{log.message}</Table.Td>
                                    <Table.Td>{log.source}</Table.Td>
                                    <Table.Td>{log.metadata?.user?.email || '-'}</Table.Td>
                                    <Table.Td>
                                        {log.metadata ? (
                                            <Spoiler 
                                                maxHeight={60} 
                                                showLabel="Show more" 
                                                hideLabel="Show less"
                                            >
                                                <pre style={{ margin: 0, fontSize: '12px' }}>
                                                    {JSON.stringify(log.metadata, null, 2)}
                                                </pre>
                                            </Spoiler>
                                        ) : '-'}
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </Box>

                <Group justify="center">
                    <Pagination
                        total={Math.ceil(total / per_page)}
                        value={page}
                        onChange={handlePageChange}
                    />
                </Group>
            </Stack>
        </>
    );
}

export default LogViewer;